import {
  DataGrid,
  type DataGridProps,
  type GridValidRowModel,
} from '@mui/x-data-grid'
import { useLayout } from 'hooks/useLayout'
import { isEmpty } from 'lodash'
import { colorPrimitives } from 'components/Theme'
import { NoDataIndicator } from './NoDataIndicator'
import { MobileDataTable } from './MobileDataTable'

export const DataTablePaging = <RowModelT extends GridValidRowModel>({
  sortFieldName,
  sort,
  totalCount,
  noDataMessage = 'No data found',
  forceDesktopTableInMobile = false,
  mobileCustomButtonText,
  onMobileCustomButtonClicked,
  emptyDataImageSource,
  columnVisibilityModel,
  ...props
}: DataGridProps<RowModelT> & {
  sortFieldName?: string
  sort?: 'asc' | 'desc'
  totalCount: number
  noDataMessage?: string
  forceDesktopTableInMobile?: boolean
  mobileCustomButtonText?: string
  onMobileCustomButtonClicked?: any
  emptyDataImageSource?: string
  columnVisibilityModel?: any
}) => {
  const { isMobile } = useLayout()
  const {
    columns,
    rows,
    onRowClick,
    paginationModel,
    onPaginationModelChange,
    onSortModelChange,
    sortModel,
    checkboxSelection,
  } = props

  if (isEmpty(rows)) {
    return (
      <NoDataIndicator
        noDataMessage={noDataMessage}
        imageSource={emptyDataImageSource}
      />
    )
  }

  if (isMobile && !forceDesktopTableInMobile) {
    return (
      <MobileDataTable
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        customButtonText={mobileCustomButtonText}
        mobileCustomButtonClicked={onMobileCustomButtonClicked}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        totalCount={totalCount}
      />
    )
  }

  return (
    <DataGrid<RowModelT>
      sx={{
        '& .MuiDataGrid-row:hover': {
          backgroundColor: colorPrimitives.grayDataTableHover,
        },

        '& .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-iconButtonContainer':
          {
            marginLeft: '2px',
            visibility: 'visible',
            width: 'auto',
            opacity: 'inherit',
          },

        '& .MuiDataGrid-row, & .MuiDataGrid-cell:first-of-type': {
          color: colorPrimitives.black,
        },
      }}
      autoHeight
      checkboxSelection={checkboxSelection}
      pagination
      paginationModel={paginationModel}
      paginationMode="server"
      onPaginationModelChange={onPaginationModelChange}
      rowCount={totalCount}
      disableRowSelectionOnClick
      disableColumnMenu
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 25 },
        },
      }}
      hideFooter={totalCount <= 25}
      pageSizeOptions={[25, 50, 100]}
      {...props}
    />
  )
}
